import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, Trash2, Check, X, EyeOff, ExternalLink, Plus,
  ArrowUp, ArrowDown, Pencil, MapPin, Tag, ImageOff,
  Eye, Phone, Mail, User, AtSign, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";

const STATUS_CHIPS = [
  { label: "Изчакващи", value: "pending" },
  { label: "Публикувани", value: "published" },
  { label: "Изтекли", value: "expired" },
  { label: "Отхвърлени", value: "rejected" },
  { label: "Всички", value: "all" },
];
const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700",
  published: "bg-green-50 text-green-700",
  expired: "bg-slate-200 text-slate-600",
  rejected: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = { pending: "Изчаква", published: "Публикувана", expired: "Изтекла", rejected: "Отхвърлена" };

function TypesManager() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const { data: types, isLoading } = useQuery({
    queryKey: ["listing-types"],
    queryFn: () => api.get("/listing-types").then((r) => r.data),
  });
  const list = types || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["listing-types"] });

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/listing-types", { name: name.trim() });
      toast.success("Видът е добавен.");
      setName("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const saveName = async (id) => {
    if (!editName.trim()) { setEditId(null); return; }
    try {
      await api.put(`/admin/listing-types/${id}`, { name: editName.trim() });
      toast.success("Видът е преименуван.");
      setEditId(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    try {
      await Promise.all([
        api.put(`/admin/listing-types/${a.id}`, { order: b.order }),
        api.put(`/admin/listing-types/${b.id}`, { order: a.order }),
      ]);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Изтриване на вид „${t.name}“?`)) return;
    try {
      await api.delete(`/admin/listing-types/${t.id}`);
      toast.success("Видът е изтрит.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mb-8 rounded-xl border border-slate-200 bg-white p-4" data-testid="listing-types-manager">
      <h3 className="mb-3 font-display text-sm font-bold text-slate-900">Видове обяви</h3>
      <form onSubmit={add} className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1">
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
            data-testid="new-listing-type-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="напр. Обмен, Дарения…"
          />
        </div>
        <button type="submit" disabled={adding} data-testid="add-listing-type-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Добави
        </button>
      </form>
      {isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : (
        <div className="flex flex-wrap gap-2">
          {list.map((t, idx) => (
            <div key={t.id} className="flex items-center gap-1 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-sm" data-testid={`listing-type-${t.slug}`}>
              {editId === t.id ? (
                <>
                  <input autoFocus className="w-28 rounded border border-slate-200 px-1.5 py-0.5 text-sm outline-none focus:border-primary" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName(t.id)} />
                  <button onClick={() => saveName(t.id)} className="rounded p-1 text-green-600 hover:bg-green-50"><Check className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setEditId(null)} className="rounded p-1 text-slate-400 hover:bg-slate-200"><X className="h-3.5 w-3.5" /></button>
                </>
              ) : (
                <>
                  <span className="font-medium text-slate-700">{t.name}</span>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded p-0.5 text-slate-400 hover:bg-slate-200 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="rounded p-0.5 text-slate-400 hover:bg-slate-200 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => { setEditId(t.id); setEditName(t.name); }} data-testid={`edit-listing-type-${t.slug}`} className="rounded p-0.5 text-slate-500 hover:bg-blue-50 hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(t)} data-testid={`delete-listing-type-${t.slug}`} className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectDialog({ listing, reason, setReason, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  const submit = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="reject-dialog-overlay" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" data-testid="reject-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 p-5">
          <h3 className="font-display text-lg font-bold text-slate-900">Отхвърляне на обява</h3>
          <p className="mt-1 line-clamp-1 text-sm text-slate-500">„{listing.title}“</p>
        </div>
        <div className="p-5">
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Причина <span className="font-normal text-slate-400">(изпраща се на подателя по имейл)</span></label>
          <textarea
            data-testid="reject-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            autoFocus
            placeholder="напр. Обявата не отговаря на правилата — липсват достатъчно детайли / неподходящо съдържание…"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <p className="mt-1.5 text-xs text-slate-400">Ако оставите полето празно, подателят получава общо известие без конкретна причина.</p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-5">
          <button onClick={onCancel} data-testid="reject-cancel" className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Отказ</button>
          <button onClick={submit} disabled={busy} data-testid="reject-confirm" className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}Отхвърли и уведоми
          </button>
        </div>
      </div>
    </div>
  );
}

function ListingPreviewModal({ listing: l, onClose, onStatus, onReject, onDelete, resolve, escapeEnabled = true }) {
  useEffect(() => {
    if (!escapeEnabled) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, escapeEnabled]);
  const images = l.images || [];
  return (    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm"
      data-testid="listing-preview-overlay"
      onClick={onClose}
    >
      <div
        className="my-6 w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
        data-testid="listing-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary"><Tag className="h-3 w-3" />{l.listing_type}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[l.status]}`}>{STATUS_LABEL[l.status]}</span>
              {l.price && <span className="text-sm font-bold text-primary" data-testid="preview-price">{l.price}</span>}
            </div>
            <h3 className="mt-2 font-display text-xl font-bold leading-snug text-slate-900" data-testid="preview-title">{l.title}</h3>
          </div>
          <button onClick={onClose} data-testid="preview-close" className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-5 p-5">
          {l.status === "rejected" && l.reject_reason && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4" data-testid="preview-reject-reason">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-red-400">Причина за отхвърляне (изпратена на подателя)</div>
              <p className="whitespace-pre-wrap break-words text-sm text-red-800">{l.reject_reason}</p>
            </div>
          )}
          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="preview-gallery">
              {images.map((src, i) => (
                <a key={i} href={resolve(src)} target="_blank" rel="noreferrer" className="block aspect-[4/3] overflow-hidden rounded-lg bg-slate-100">
                  <img src={resolve(src)} alt={`Снимка ${i + 1}`} className="h-full w-full object-cover transition-transform hover:scale-105" />
                </a>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{l.location}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" />Подадена: {formatDateTime(l.created_at)}</span>
            {l.published_at && <span>Публикувана: {formatDateTime(l.published_at)}</span>}
            {l.expires_at && (l.status === "published" || l.status === "expired") && (
              <span className={l.status === "expired" ? "font-semibold text-slate-500" : ""}>
                {l.status === "expired" ? "Изтекла: " : "Валидна до: "}{formatDateTime(l.expires_at)}
              </span>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Пълно описание</div>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700" data-testid="preview-description">{l.description}</p>
          </div>

          <div className="rounded-xl bg-slate-50 p-4" data-testid="preview-contacts">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Контакти на подателя</div>
            <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
              <span className="inline-flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{l.contact_name || "—"}</span>
              {l.contact_phone && <a href={`tel:${l.contact_phone}`} className="inline-flex items-center gap-2 hover:text-primary"><Phone className="h-4 w-4 text-slate-400" />{l.contact_phone}</a>}
              {l.contact_email && <a href={`mailto:${l.contact_email}`} className="inline-flex items-center gap-2 break-all hover:text-primary"><Mail className="h-4 w-4 text-slate-400" />{l.contact_email}</a>}
              {l.contact_social && <span className="inline-flex items-center gap-2 break-all"><AtSign className="h-4 w-4 text-slate-400" />{l.contact_social}</span>}
            </div>
            {l.owner_email && <div className="mt-3 border-t border-slate-200 pt-2 text-xs text-slate-500">Регистриран профил: {l.owner_email}</div>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 p-5">
          {l.status !== "published" && (
            <button onClick={() => onStatus(l.id, "published")} data-testid="preview-approve" className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-700"><Check className="h-4 w-4" />{l.status === "expired" ? "Поднови" : "Публикувай"}</button>
          )}
          {l.status !== "rejected" && (
            <button onClick={() => onReject(l)} data-testid="preview-reject" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600"><EyeOff className="h-4 w-4" />Отхвърли</button>
          )}
          {l.status === "published" && (
            <a href={`/obyava/${l.slug}`} target="_blank" rel="noreferrer" data-testid="preview-view" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-primary hover:text-primary"><ExternalLink className="h-4 w-4" />Виж на сайта</a>
          )}
          <button onClick={() => onDelete(l.id)} data-testid="preview-delete" className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" />Изтрий</button>
        </div>
      </div>
    </div>
  );
}

export default function ListingsManager() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [preview, setPreview] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);

  const { data: listings, isLoading } = useQuery({
    queryKey: ["admin-listings", status],
    queryFn: () => api.get(`/admin/listings`, { params: status === "all" ? {} : { status } }).then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-listings"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
    qc.invalidateQueries({ queryKey: ["listings"] });
  };

  const setListingStatus = async (id, newStatus, reason = "") => {
    try {
      const { data } = await api.put(`/admin/listings/${id}/status`, { status: newStatus, reason });
      const msg = newStatus === "published"
        ? (data?.notified ? "Обявата е публикувана. Подателят е уведомен по имейл." : "Обявата е публикувана.")
        : newStatus === "rejected"
          ? (data?.notified ? "Обявата е отхвърлена. Подателят е уведомен по имейл." : "Обявата е отхвърлена.")
          : "Статусът е обновен.";
      toast.success(msg);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Изтриване на обявата?")) return;
    try {
      await api.delete(`/admin/listings/${id}`);
      toast.success("Обявата е изтрита.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const list = listings || [];

  const onStatusFromModal = async (id, s) => { await setListingStatus(id, s); setPreview(null); };
  const onDeleteFromModal = async (id) => { await del(id); setPreview(null); };
  const openReject = (l) => { setRejectReason(""); setRejectFor(l); };
  const confirmReject = async () => {
    if (!rejectFor) return;
    await setListingStatus(rejectFor.id, "rejected", rejectReason);
    setRejectFor(null);
    setRejectReason("");
    setPreview(null);
  };

  return (
    <div className="mt-6" data-testid="listings-manager">
      {isAdmin && <TypesManager />}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Обяви</h2>
        <div className="flex flex-wrap gap-2">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.value}
              onClick={() => setStatus(c.value)}
              data-testid={`listings-filter-${c.value}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                status === c.value ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500">
          Няма обяви в тази категория.
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((l) => (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-listing-${l.id}`}>
              <div className="flex gap-4">
                <div className="hidden h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-slate-100 sm:block">
                  {(l.images || [])[0] ? (
                    <img src={resolve(l.images[0])} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-300"><ImageOff className="h-6 w-6" /></div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary"><Tag className="h-3 w-3" />{l.listing_type}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[l.status]}`}>{STATUS_LABEL[l.status]}</span>
                    {l.price && <span className="text-sm font-bold text-primary">{l.price}</span>}
                  </div>
                  <h3 onClick={() => setPreview(l)} className="mt-1 cursor-pointer font-display text-base font-bold text-slate-900 hover:text-primary">{l.title}</h3>
                  <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{l.location}</span>
                    <span>{l.contact_name}{l.contact_phone ? ` · ${l.contact_phone}` : ""}</span>
                    <span>{formatDateTime(l.created_at)}</span>
                    {l.expires_at && (l.status === "published" || l.status === "expired") && (
                      <span className={l.status === "expired" ? "font-semibold text-slate-500" : ""}>
                        {l.status === "expired" ? "изтекла на " : "валидна до "}{formatDateTime(l.expires_at)}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{l.description}</p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <button onClick={() => setPreview(l)} data-testid={`listing-preview-${l.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/10">
                  <Eye className="h-3.5 w-3.5" />Преглед
                </button>
                {l.status !== "published" && (
                  <button onClick={() => setListingStatus(l.id, "published")} data-testid={`listing-approve-${l.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700">
                    <Check className="h-3.5 w-3.5" />{l.status === "expired" ? "Поднови" : "Публикувай"}
                  </button>
                )}
                {l.status !== "rejected" && (
                  <button onClick={() => openReject(l)} data-testid={`listing-reject-${l.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-red-300 hover:text-red-600">
                    <EyeOff className="h-3.5 w-3.5" />Отхвърли
                  </button>
                )}
                {l.status === "published" && (
                  <a href={`/obyava/${l.slug}`} target="_blank" rel="noreferrer" data-testid={`listing-view-${l.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary">
                    <ExternalLink className="h-3.5 w-3.5" />Виж
                  </a>
                )}
                <button onClick={() => del(l.id)} data-testid={`listing-delete-${l.id}`} className="ml-auto inline-flex items-center gap-1.5 rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <ListingPreviewModal
          listing={preview}
          onClose={() => setPreview(null)}
          onStatus={onStatusFromModal}
          onReject={openReject}
          onDelete={onDeleteFromModal}
          resolve={resolve}
          escapeEnabled={!rejectFor}
        />
      )}

      {rejectFor && (
        <RejectDialog
          listing={rejectFor}
          reason={rejectReason}
          setReason={setRejectReason}
          onCancel={() => setRejectFor(null)}
          onConfirm={confirmReject}
        />
      )}
    </div>
  );
}
